import "./App.css";

import { PrimeReactProvider } from "primereact/api";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.css";
import "primeflex/primeflex.css";
import "primeicons/primeicons.css";
import Case from "./MedicalCases/Case";
import CaseMasive from "./MedicalCases/CaseMasive";
import Person from "./Person/Person";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AdvanceListing from "./Advances/AdvanceListing";
import MedicalStaffSelector from "./MedicalStaff/MedicalStaffSelector";
import MedicalCenterAdvances from "./Advances/MedicalCenterAdvances";
import CreateAdvance from "./Advances/CreateAdvance";
import MedicalCentersListing from "./MedicalCenters/MedicalCentersListing";
import RequirementsListing from "./Requirements/RequirementsListing";
import CaseDetail from "./MedicalCases/CaseDetail";
import InvoiceList from "./Invoices/InvoiceList";
import CreateInvoice from "./Invoices/CreateInvoice";
import CreateMedicalCenter from "./MedicalCenters/CreateMedicalCenter";
import Login from "./Login/Login";
import Users from "./ManageUsers/Users";
import UsersClient from "./ManageUsers/UsersClient";
import MedicalCasesListing from "./MedicalCases/MedicalCasesListing";
// import PageConstruction from "./PageConstruction/PageConstruction";
// import BaremosListing from "./Baremos/BaremosListing";
import EditMedicalCenter from "./MedicalCenters/EditMedicalCenter";
import EditBaremo from "./Baremos/EditBaremo";
import CreateBaremo from "./Baremos/CreateBaremo";
import Logout from "./Logout/Logout";
import MedicalCasesSubscribeList from "./MedicalCases/Subscribe/MedicalCasesSubscribeList";
import { PAYMENTS_ROUTE, ROUTE_ALLOWED_ROLES, UserRole } from "./constants";
import MedicalCaseReportList from "./Reports/MedicalCaseReportList";
import Maintenance from "./Maintenance/Maintenance";
import { addLocale, locale } from "primereact/api";
import ErrorPage from "./NotFound/ErrorPage";
import PersonListing from "./Person/PersonListing";
import RMSPatient from "./RMS/RmsPatient";
import TermsConditions from "./TermsConditions/TermsConditions";
import PrivacyPolicies from "./TermsConditions/PrivacyPolicies";
import AssignedCasesListing from "./MedicalCases/SubAdministrator/AssignedCases";
import { useRef, useState } from "react";
import { ToastContext, ToastWithRef } from "./ToastContext";
import EditCase from "./MedicalCases/EditCase";
import ReassignCase from "./MedicalCases/SubAdministrator/ReassignCase";
import StaleCasesListing from "./MedicalCases/StaleCasesListing";
import ScaleSelectClient from "./Baremos/ScaleSelectClient";
import ClientScaleView from "./Baremos/ClientScaleView";
import Balance from "./Balance/Balance";
import AnnulledCasesList from "./MedicalCases/AnnulledCasesList";
import PaymentSelectClient from "./Payment/PaymentSelectClient";
import ClientPaymentView from "./Payment/ClientPaymentView";
import PaymentDetailView from "./Payment/PaymentDetailView";
import GenerateToken from "./Token/generateToken";
import Chat from "./Chat/Chat";
import ChatUpdater from "./ChatUpdater";
import { NotificationBadgeContext } from "./NotificationBadgeContext";
import { useIsInsolvent } from "./hooks/queries";
import PendingRequestList from "./MedicalCases/CaseRequest/PendingRequestList";
import AssignCaseRequest from "./MedicalCases/CaseRequest/AssignCaseRequest";
import FundListing from "./Funds/FundListing";
import  CreateFund  from "./Funds/CreateFund";
// const localeES = require("./locales/es.json");

locale("es");
addLocale("es", {
  firstDayOfWeek: 1,
  dayNames: [
    "domingo",
    "lunes",
    "martes",
    "miércoles",
    "jueves",
    "viernes",
    "sábado",
  ],
  dayNamesShort: ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"],
  dayNamesMin: ["D", "L", "M", "X", "J", "V", "S"],
  monthNames: [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ],
  monthNamesShort: [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ],
  today: "Hoy",
  clear: "Limpiar",
  emptyFilterMessage: "Sin opciones disponibles",
  emptyMessage: "No se han encontrado resultados",
  emptySearchMessage: "Sin opciones disponibles",
});

/**
 * Redirects to a page if the user is not authenticated
 *
 * Taken from https://stackoverflow.com/a/69907150/13784002
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} props.path Route to access
 * @param {string} props.redirectTo Route to redirect if user is not authenticated or authorized
 * @param {boolean?} props.withPaymentProtection Wether to include payment protection or not
 */
function ProtectedRoute({
  children,
  path,
  redirectTo = "/login",
  withPaymentProtection = true,
}) {
  if (!path) {
    return <Navigate to={redirectTo} />;
  }

  const isAuthenticated = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  const isAdmin = localStorage.getItem("isAdmin");
  // cast string to boolean
  // if (isAuthenticated && isAdmin==="false" ) {
  //   return <Navigate to="/mantenimiento" />;
  // }

  if (isAdmin === "true") {
    return children;
  }

  // if (role === UserRole.MASTER) {
  //   return children;
  // }

  const roles = ROUTE_ALLOWED_ROLES[path] || [];

  return isAuthenticated && roles.includes(role) ? (
    withPaymentProtection ? (
      <PaymentProtectedRoute>{children}</PaymentProtectedRoute>
    ) : (
      children
    )
  ) : (
    <Navigate to={redirectTo} />
  );
}

/**
 * Redirect to payment page if user is missing payment
 *
 * Assumes user is authenticated
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function PaymentProtectedRoute({ children }) {
  const role = localStorage.getItem("role");
  const isAdmin = localStorage.getItem("isAdmin") === "true";

  const insolvent = useIsInsolvent({
    enabled:
      [UserRole.MEDICAL_CENTER, UserRole.SUB_ADMINISTRATOR].includes(role) &&
      !isAdmin,
    initialData: { is_insolvent: false },
  });

  if (insolvent.data.is_insolvent) {
    return (
      <Navigate to={PAYMENTS_ROUTE} state={{ showInsolventDialog: true }} />
    );
  } else {
    return children;
  }
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <Login />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/logout",
    element: <Logout />,
  },
  {
    path: "/usuarios",
    element: (
      <ProtectedRoute path="/usuarios">
        <Users />
      </ProtectedRoute>
    ),
  },
  {
    path: "/usuarios/cliente",
    element: (
      <ProtectedRoute path="/usuarios">
        <UsersClient />
      </ProtectedRoute>
    ),
  },
  {
    path: "/persona/editar/:personId",
    element: (
      <ProtectedRoute path="/casos/nuevo">
        <Person />
      </ProtectedRoute>
    ),
  },
  {
    path: "/asegurados",
    element: (
      <ProtectedRoute path="/asegurados">
        <PersonListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/rms",
    element: (
      <ProtectedRoute path="/rms">
        <RMSPatient />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos",
    element: (
      <ProtectedRoute path="/casos">
        <MedicalCasesListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/anulados",
    element: (
      <ProtectedRoute path="/casos/anulados">
        <AnnulledCasesList />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/nuevo",
    element: (
      <ProtectedRoute path="/casos/nuevo">
        <Case />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/nuevo/:caseId",
    element: (
      <ProtectedRoute path="/casos/nuevo">
        <Case />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/:caseId/editar",
    element: (
      <ProtectedRoute path="/casos/:caseId/editar">
        <EditCase />
      </ProtectedRoute>
    ),
  },
  // {
  //   path: "/casos/masivo/nuevo",
  //   element: (
  //     <ProtectedRoute path="/casos/nuevo">
  //       <CaseMasive/>
  //     </ProtectedRoute>
  //   ),
  // },
  {
    path: "/casos/carga-masiva",
    element: (
      <ProtectedRoute path="/casos/carga-masiva">
        <CaseMasive />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/:caseId",
    element: (
      <ProtectedRoute path="/casos/:caseId">
        <CaseDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/:caseId/reasignar",
    element: (
      <ProtectedRoute path="/casos/:caseId/reasignar">
        <ReassignCase />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/reasignados/:caseId",
    element: (
      <ProtectedRoute path="/casos/reasignados/:caseId">
        <CaseDetail />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/por-suscribir",
    element: (
      <ProtectedRoute path="/casos/por-suscribir">
        <MedicalCasesSubscribeList />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/por-suscribir/:caseId",
    element: (
      <ProtectedRoute path="/casos/por-suscribir/:caseId">
        <CaseDetail backButtonUrl="/casos/por-suscribir" />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/reasignados",
    element: (
      <ProtectedRoute path="/casos/reasignados">
        <AssignedCasesListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/solicitudes-pendientes",
    element: (
      <ProtectedRoute path="/casos/solicitudes-pendientes">
        <PendingRequestList />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos/:caseId/asignar-solicitud",
    element: (
      <ProtectedRoute path="/casos/:caseId/asignar-solicitud">
        <AssignCaseRequest />
      </ProtectedRoute>
    ),
  },
  {
    path: "/medicos",
    element: (
      <ProtectedRoute path="/medicos">
        <MedicalStaffSelector />
      </ProtectedRoute>
    ),
  },
  {
    path: "/casos-pendientes",
    element: (
      <ProtectedRoute path="/casos-pendientes">
        <StaleCasesListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/anticipos",
    element: (
      <ProtectedRoute path="/anticipos">
        <AdvanceListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/anticipos/nuevo",
    element: (
      <ProtectedRoute path="/anticipos/nuevo">
        <CreateAdvance />
      </ProtectedRoute>
    ),
  },
  {
    path: "/anticipos/proveedores/:centerId",
    element: (
      <ProtectedRoute path="/anticipos/proveedores/:centerId">
        <MedicalCenterAdvances />
      </ProtectedRoute>
    ),
  },
  {
    path: "/baremos",
    element: (
      <ProtectedRoute path="/baremos">
        <ScaleSelectClient />
      </ProtectedRoute>
    ),
  },
  {
    path: "/baremos/:clientId",
    element: (
      <ProtectedRoute path="/baremos/:clientId">
        <ClientScaleView />
      </ProtectedRoute>
    ),
  },
  {
    path: "/baremos/nuevo",
    element: (
      <ProtectedRoute path="/baremos/nuevo">
        <CreateBaremo />
      </ProtectedRoute>
    ),
  },
  {
    path: "/baremos/:baremoId",
    element: (
      <ProtectedRoute path="/baremos/:baremoId">
        <EditBaremo />
      </ProtectedRoute>
    ),
  },
  {
    path: "/saldo",
    element: (
      <ProtectedRoute path="/saldo">
        <Balance />
      </ProtectedRoute>
    ),
  },
  {
    path: "/pagos/clientes",
    element: (
      <ProtectedRoute path="/pagos/clientes" withPaymentProtection={false}>
        <PaymentSelectClient />
      </ProtectedRoute>
    ),
  },
  {
    path: "/pagos/clientes/:clientId",
    element: (
      <ProtectedRoute
        path="/pagos/clientes/:clientId"
        withPaymentProtection={false}
      >
        <ClientPaymentView />
      </ProtectedRoute>
    ),
  },
  {
    path: "/pagos/detalle/:billingID",
    element: (
      <ProtectedRoute
        path="/pagos/detalle/:billingID"
        withPaymentProtection={false}
      >
        <PaymentDetailView />
      </ProtectedRoute>
    ),
  },
  {
    path: "/proveedores",
    element: (
      <ProtectedRoute path="/proveedores">
        <MedicalCentersListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/proveedores/nuevo",
    element: (
      <ProtectedRoute path="/proveedores/nuevo">
        <CreateMedicalCenter />
      </ProtectedRoute>
    ),
  },
  {
    path: "/proveedores/editar/:centerId",
    element: (
      <ProtectedRoute path="/proveedores/editar/:centerId">
        <EditMedicalCenter />
      </ProtectedRoute>
    ),
  },
  {
    path: "/requerimientos",
    element: (
      <ProtectedRoute path="/requerimientos">
        <RequirementsListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/centro-medico/facturas",
    element: (
      <ProtectedRoute path="/centro-medico/facturas">
        <InvoiceList />
      </ProtectedRoute>
    ),
  },
  {
    path: "/centro-medico/facturas/nueva",
    element: (
      <ProtectedRoute path="/centro-medico/facturas/nueva">
        <CreateInvoice />
      </ProtectedRoute>
    ),
  },
  {
    path: "/centro-medico/servicios",
    element: (
      <ProtectedRoute path="/centro-medico/servicios">
        <CreateMedicalCenter />
      </ProtectedRoute>
    ),
  },
  {
    path: "/reportes/casos-medicos",
    element: (
      <ProtectedRoute path="/casos">
        <MedicalCaseReportList />
      </ProtectedRoute>
    ),
  },
  {
    path: "/chat",
    element: (
      <ProtectedRoute path="/chat">
        <Chat />
      </ProtectedRoute>
    ),
  },
  {
    path: "/mantenimiento",
    element: <Maintenance />,
  },
  {
    path: "/terminos-y-condiciones",
    element: <TermsConditions />,
  },
  {
    path: "/politicas-de-privacidad",
    element: <PrivacyPolicies />,
  },
  {
    path: "/api/generar-token",
    element: (
      <ProtectedRoute path="/api/generar-token">
        <GenerateToken />
      </ProtectedRoute>
    ),
  },
  {
    path: "/fondo-incurrido/abonos",
    element: (
      <ProtectedRoute path="/fondo-incurrido/abonos">
        <FundListing />
      </ProtectedRoute>
    ),
  },
  {
    path: "/fondo-incurrido/crear-pago",
    element: (
      <ProtectedRoute path="/fondo-incurrido/crear-pago">
        <CreateFund />
      </ProtectedRoute>
    ),
  },
]);

const queryClient = new QueryClient();

function App() {
  // useEffect(() => {
  //   const updateServiceWorker = (registration) => {
  //     if (registration.waiting) {
  //       registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  //       registration.waiting.addEventListener('statechange', (event) => {
  //         console.log("UpdateServiceWorker", event);
  //         if (event.target.state === 'activated') {
  //           console.log("activated");
  //           // window.location.reload();
  //         }
  //       });
  //     }
  //   };

  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker.register('/service-worker.js').then((registration) => {
  //       console.log("firstInstall", registration);
  //       registration.addEventListener('updatefound', () => {
  //         console.log(registration)
  //         updateServiceWorker(registration);
  //       });
  //     });
  //   }
  // }, []);
  const toastRef = useRef(null);

  const notificationState = useState(
    JSON.parse(localStorage.getItem("notificationState")) || {}
  );

  return (
    <PrimeReactProvider>
      <QueryClientProvider client={queryClient}>
        <ToastContext.Provider value={toastRef}>
          <NotificationBadgeContext.Provider value={notificationState}>
            <ToastWithRef />
            <ChatUpdater />
            <RouterProvider router={router} />
          </NotificationBadgeContext.Provider>
        </ToastContext.Provider>
      </QueryClientProvider>
    </PrimeReactProvider>
  );
}

export default App;
